import { useCallback, useState } from "react";
import { useUploadImage } from "arkh";
import { graphql, useMutation } from "react-relay";
import { useSaveNoteImageGetImageLinkMutation } from "./__generated__/useSaveNoteImageGetImageLinkMutation.graphql";
import { useSaveNoteImageMutation } from "./__generated__/useSaveNoteImageMutation.graphql";
import { useSaveNoteImageOrgGetImageLinkMutation } from "./__generated__/useSaveNoteImageOrgGetImageLinkMutation.graphql";

export function useSaveNoteImage(noteId: string) {
  const [setNoteImage, info] = useMutation<useSaveNoteImageMutation>(graphql`
    mutation useSaveNoteImageMutation(
      $noteId: ID!
      $pageId: ID!
      $layout: WidgetNoteUploadImageLayoutInput!
      $imageId: ID!
    ) @raw_response_type {
      note(id: $noteId) {
        page(id: $pageId) {
          setImage(layout: $layout, imageId: $imageId) {
            id
            image {
              id

              ...PageSettingsDeleteButton_image
            }
            ...WidgetNotePageBackground_widgetNotePage
            imageScale
            imageTranslateX
            imageTranslateY
          }
        }
      }
    }
  `);

  const onSetNoteImage = useCallback(
    (
      pageId: string,
      uri: string,
      imageId: string,
      layout: {
        translateX: number;
        translateY: number;
        scale: number;
        height: number;
        width: number;
      }
    ) => {
      setNoteImage({
        variables: {
          noteId,
          pageId,
          imageId,
          layout: {
            translateX: layout.translateX,
            translateY: layout.translateY,
            scale: layout.scale,
          },
        },

        updater: (store, response) => {
          const imageRef = response?.note.page.setImage.image;
          if (imageRef == null) {
            return;
          }
          const largeImage = store.create(
            imageRef.id + "largeImage",
            "ImageSize"
          );
          largeImage.setValue(uri, "url");
          largeImage.setValue(layout.width, "width");
          largeImage.setValue(layout.height, "height");
          store.get(imageRef.id)?.setLinkedRecord(largeImage, "size", {
            minHeight: 1080,
            minWidth: 1080,
          });
        },
      });
    },
    [setNoteImage, noteId]
  );

  return [onSetNoteImage, info] as const;
}

export function useUploadWidgetNoteImageOrg(workspaceId: string) {
  const [generateOrgImageLink] =
    useMutation<useSaveNoteImageOrgGetImageLinkMutation>(graphql`
      mutation useSaveNoteImageOrgGetImageLinkMutation($workspaceId: ID!) {
        workspace(id: $workspaceId) {
          uploadImageLink {
            image {
              id
            }
            url
          }
        }
      }
    `);

  const { uploadImage, prepareForImageUpload } = useUploadImage();

  const saveImage = useCallback(
    (
      localUri: string,
      { width, height }: { width: number; height: number }
    ) => {
      const uploadIdRequest = prepareForImageUpload({
        width,
        height,
        localImageURI: localUri,
      });
      return new Promise<{ localImageURI: string; imageId: string }>(
        (resolve, reject) =>
          generateOrgImageLink({
            variables: {
              workspaceId,
            },
            onError: (e) => {
              reject(e);
            },
            onCompleted: (data) => {
              const uploadLink = data.workspace?.uploadImageLink;
              uploadIdRequest.then((uploadId) => {
                if (uploadLink?.image.id) {
                  resolve({
                    imageId: uploadLink.image.id,
                    localImageURI: localUri,
                  });
                  uploadImage({
                    imageId: uploadLink.image.id,
                    uploadURL: uploadLink.url,
                    uploadId,
                  });
                } else {
                  reject(new Error());
                }
              });
            },
          })
      );
    },
    [generateOrgImageLink, prepareForImageUpload, uploadImage, workspaceId]
  );

  return saveImage;
}

export function useUploadWidgetNoteImage() {
  const { uploadImage, prepareForImageUpload } = useUploadImage();

  const [status] = useState<
    | { status: "pending" }
    | { status: "uploading"; imageId: string; uploadUrl: string }
    | { status: "ready"; imageId: string; uploadUrl: string }
  >({ status: "pending" });
  const [generateImageLink, info] =
    useMutation<useSaveNoteImageGetImageLinkMutation>(graphql`
      mutation useSaveNoteImageGetImageLinkMutation {
        uploadImageLink {
          image {
            id
          }
          url
        }
      }
    `);

  const saveImage = useCallback(
    (
      localUri: string,
      { width, height }: { width: number; height: number }
    ) => {
      const uploadIdRequest = prepareForImageUpload({
        width,
        height,
        localImageURI: localUri,
      });
      return new Promise<{ localImageURI: string; imageId: string }>(
        (resolve, reject) =>
          generateImageLink({
            variables: {},
            onError: (e) => {
              reject(e);
            },
            onCompleted: (data) => {
              uploadIdRequest.then((uploadId) => {
                if (data.uploadImageLink?.image.id) {
                  resolve({
                    imageId: data.uploadImageLink.image.id,
                    localImageURI: localUri,
                  });
                  uploadImage({
                    imageId: data.uploadImageLink.image.id,
                    uploadURL: data.uploadImageLink.url,
                    uploadId,
                  });
                } else {
                  reject(new Error());
                }
              });
            },
          })
      );
    },
    [generateImageLink, prepareForImageUpload, uploadImage]
  );

  return [saveImage, info, status] as const;
}
